import React, { useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'

const BAUD_RATES = ['300','1200','2400','4800','9600','19200','38400','57600','115200','230400']
const LINE_ENDINGS = [
    { label: 'None',   value: 'none' },
    { label: 'NL',     value: 'nl' },
    { label: 'CR',     value: 'cr' },
    { label: 'NL+CR',  value: 'nlcr' },
]

const selectStyle = {
    fontSize: 12, padding: '2px 4px',
    border: '1px solid #d1d5db', borderRadius: 6,
    background: 'white', color: '#374151', cursor: 'pointer',
    outline: 'none',
}

const iconBtnStyle = {
    background: 'white', border: 'none', cursor: 'pointer',
    borderRadius: 8, padding: '2px 4px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#6B7280',
}

function SerialMonitor ({ worker }) {
    const [logs, setLogs]           = useState([])
    const [inputValue, setInputValue] = useState('')
    const [baudRate, setBaudRate]   = useState('9600')
    const [lineEnding, setLineEnding] = useState('none')
    const [timeEnabled, setTimeEnabled] = useState(false)
    const textareaRef = useRef(null)

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight
        }
    }, [logs])

    useEffect(() => {
        const handleMessage = (e) => {
            if (e.data.type === 'read') {
                const text = String.fromCharCode(
                    ...e.data.value.split(',').map(Number)
                )
                const entry = timeEnabled
                    ? `${new Date().toLocaleTimeString()} -> ${text}`
                    : text
                setLogs(prev => {
                    const updated = [...prev, entry]
                    return updated.length > 700 ? updated.slice(200) : updated
                })
            }
        }
        if (worker) worker.addEventListener('message', handleMessage)
        return () => { if (worker) worker.removeEventListener('message', handleMessage) }
    }, [worker, timeEnabled])

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!inputValue.trim()) return
        let cmd = inputValue
        if (lineEnding === 'nl')   cmd += '\n'
        if (lineEnding === 'cr')   cmd += '\r'
        if (lineEnding === 'nlcr') cmd += '\r\n'
        if (worker) worker.postMessage({ type: 'write', value: cmd })
        setLogs(prev => [...prev, `$ ${inputValue}\n`])
        setInputValue('')
    }

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', background: '#0D1117', overflow: 'hidden' }}>

            {/* Header — matches scripting's bg-[#FCFCFD] header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 12px', background: '#FCFCFD',
                borderBottom: '1px solid #f3f4f6', flexShrink: 0, minHeight: 44, gap: 12,
            }}>
                <span style={{ color: '#535862', fontSize: 13, fontWeight: 600, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                    Serial Monitor
                </span>

                {/* Baud rate */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Baud rate</label>
                    <select value={baudRate} onChange={e => setBaudRate(e.target.value)} style={selectStyle}>
                        {BAUD_RATES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                {/* Line ending */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Line ending</label>
                    <select value={lineEnding} onChange={e => setLineEnding(e.target.value)} style={selectStyle}>
                        {LINE_ENDINGS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                {/* Icon buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                    {/* Timestamp toggle */}
                    <button
                        onClick={() => setTimeEnabled(v => !v)}
                        title="Toggle timestamp"
                        style={{ ...iconBtnStyle, background: timeEnabled ? '#f3f4f6' : 'white' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                    </button>

                    {/* Clear */}
                    <button onClick={() => setLogs([])} title="Clear monitor" style={iconBtnStyle}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Log output */}
            <div style={{ flex: 1, height: 'calc(100% - 37px)', width: '100%', padding: '12px', overflow: 'auto', boxSizing: 'border-box' }}>
                <textarea
                    ref={textareaRef}
                    value={logs.join('')}
                    readOnly
                    style={{
                        width: '100%', height: '100%', resize: 'none',
                        background: 'transparent', color: '#ffffff',
                        fontFamily: 'monospace', fontSize: 13,
                        outline: 'none', border: 'none', lineHeight: 1.5,
                    }}
                />
            </div>

            {/* Command input */}
            <form onSubmit={handleSubmit} style={{ width: '100%', borderTop: '1px solid #374151', flexShrink: 0 }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    placeholder="Enter command..."
                    style={{
                        width: '100%', padding: '8px 12px',
                        background: '#111827', color: '#ffffff',
                        fontFamily: 'monospace', fontSize: 13,
                        outline: 'none', border: 'none', boxSizing: 'border-box',
                    }}
                />
            </form>
        </div>
    )
}

const mapStateToProps = state => ({ worker: state.scratchGui.worker })
export default connect(mapStateToProps)(SerialMonitor)
